import { useEffect, useState } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import {
  useQueryState,
  IconLoader,
  ButtonPrimary,
  IconExport,
  ButtonSize,
  SchemaFilterType,
  SortDir,
  PermissionEnum,
} from '@ui/src';
import { useAppDispatch, useAppSelector } from '@store';
import {
  setAccountId as setAccountIdAction,
  clearCardsStamenetState,
} from '../store/slices/accounts-statements';
import { overviewAccountsSchema } from '../../individual/variables/overview-accounts';
import { StatementsContextType } from '../types';
import OverviewStatementsTransaction from '../../../components/partial/overview-statements/overview-statements-trancations';
import useStatements from '../hooks/use-statements';
import StatementPanel from '../statements/statements-panel';
import { AccountTransactionsTypes } from './types';

import cn from '../customers.module.css';

const AccountTransactions = ({
  customerType,
  accountsPage,
}: AccountTransactionsTypes) => {
  const { accounts, currentAccount, balances } =
    useOutletContext<StatementsContextType>();

  const dispatch = useAppDispatch();

  const {
    customerAccountsStatements: { accountsStatements },
    customersDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const { filters, sort, onSortChange, onFilterChange } = useQueryState(
    overviewAccountsSchema,
  );
  const [statementPanelIsVisible, setStatementPanelIsVisible] = useState(false);

  const { customerId } = useParams();

  const {
    setAccountId,
    isLoading,
    isFetching,
    onSort,
    onFilter,
    formattedData,
    accountId,
    errorList,
    statementError,
  } = useStatements({
    statements: accountsStatements,
    filters,
    onSortChange,
    onFilterChange,
    sort,
    customerId,
    customerType,
    accountsPage,
    accountNumber: currentAccount?.fullNumber,
  });

  useEffect(() => {
    dispatch(clearCardsStamenetState());
  }, [dispatch]);

  useEffect(() => {
    if (accounts && accounts.length && !currentAccount) {
      setAccountId(accounts[0].id);
      dispatch(setAccountIdAction(accounts[0].id));

      onFilter({
        ...filters,
        account: {
          value: [accounts[0].fullNumber],
          type: SchemaFilterType.STRING,
        },
        sortProp: {
          value: ['transferDate'],
          type: SchemaFilterType.STRING,
        },
        sortDir: {
          value: [SortDir.DESC],
          type: SchemaFilterType.STRING,
        },
      });
    } else if (currentAccount) {
      setAccountId(currentAccount.id);
      dispatch(setAccountIdAction(currentAccount.id));

      onFilter({
        ...filters,
        account: {
          value: [currentAccount.fullNumber],
          type: SchemaFilterType.STRING,
        },
        sortProp: {
          value: ['transferDate'],
          type: SchemaFilterType.STRING,
        },
        sortDir: {
          value: [SortDir.DESC],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [accounts, filters, dispatch, currentAccount]);

  let content;

  if (isLoading) {
    content = (
      <div className={cn.overviewStatementsLoader}>
        <IconLoader />
      </div>
    );
  } else {
    content = (
      <OverviewStatementsTransaction
        onSort={onSort}
        accountId={accountId}
        onFilter={onFilter}
        data={formattedData}
        filters={filters}
        sort={sort}
        requestInProgress={isFetching}
        errorList={errorList}
      />
    );
  }

  return (
    <>
      {accounts && (
        <div className={cn.overviewStatementsAccountsWrapper}>
          {accounts.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`${cn.overviewStatementsAccountButton} ${
                accountId === item.id
                  ? cn.overviewStatementsAccountButtonActive
                  : ''
              }`}
              onClick={() => {
                setAccountId(item.id);
                dispatch(setAccountIdAction(item.id));

                onFilter({
                  ...filters,
                  account: {
                    value: [item.fullNumber],
                    type: SchemaFilterType.STRING,
                  },
                  sortProp: {
                    value: ['transferDate'],
                    type: SchemaFilterType.STRING,
                  },
                  sortDir: {
                    value: [SortDir.DESC],
                    type: SchemaFilterType.STRING,
                  },
                });
              }}
            >
              {item.fullNumber}
            </button>
          ))}
        </div>
      )}
      <div className={cn.accountsStatementsControls}>
        {activePermissions?.includes(
          PermissionEnum.ACCOUNTS_STATEMENTS_EXPORT_ACTION,
        ) && (
          <ButtonPrimary
            icon={<IconExport />}
            size={ButtonSize.SMALL}
            text="Export"
            onClick={() => setStatementPanelIsVisible(true)}
            className={cn.exportButton}
          />
        )}
        {statementError && (
          <div className={cn.downloadStatementError}>{statementError}</div>
        )}
      </div>
      <StatementPanel
        statementPanelVisible={statementPanelIsVisible}
        onCloseStatementPanel={() => setStatementPanelIsVisible(false)}
        selectedAccount={currentAccount?.id}
        accounts={accounts}
        currencies={balances
          ?.filter((item) => item.accountId === currentAccount?.id)
          ?.map((item) => item.currencyId)}
      />
      <div
        className={`${cn.overviewStatementsContentTable} ${
          isFetching ? cn.tableLoading : ''
        }`}
      >
        {content}
      </div>
      <Outlet />
    </>
  );
};

export default AccountTransactions;
